import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── Same WebGL shader as Hero (brand palette) ───────────────────────────────
const defaultShaderSource = `#version 300 es
precision highp float;
out vec4 O;
uniform vec2 resolution;
uniform float time;
#define FC gl_FragCoord.xy
#define T time
#define R resolution
#define MN min(R.x,R.y)

float rnd(vec2 p) {
  p=fract(p*vec2(12.9898,78.233));
  p+=dot(p,p+34.56);
  return fract(p.x*p.y);
}
float noise(in vec2 p) {
  vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
  float a=rnd(i), b=rnd(i+vec2(1,0)), c=rnd(i+vec2(0,1)), d=rnd(i+1.);
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
float fbm(vec2 p) {
  float t=.0, a=1.; mat2 m=mat2(1.,-.5,.2,1.2);
  for (int i=0; i<5; i++) { t+=a*noise(p); p*=2.*m; a*=.5; }
  return t;
}
float clouds(vec2 p) {
  float d=1., t=.0;
  for (float i=.0; i<3.; i++) {
    float a=d*fbm(i*10.+p.x*.2+.2*(1.+i)*p.y+d+i*i+p);
    t=mix(t,d,a); d=a; p*=2./(i+1.);
  }
  return t;
}
void main(void) {
  vec2 uv=(FC-.5*R)/MN, st=uv*vec2(2,1);
  vec3 col=vec3(0);
  vec3 navy  = vec3(0.137, 0.208, 0.349);
  vec3 purple= vec3(0.490, 0.275, 0.549);
  vec3 teal  = vec3(0.306, 0.749, 0.749);
  vec3 coral = vec3(0.949, 0.471, 0.361);
  float bg = clouds(vec2(st.x+T*.5, -st.y));
  uv *= 1.-.3*(sin(T*.2)*.5+.5);
  for (float i=1.; i<12.; i++) {
    uv += .1*cos(i*vec2(.1+.01*i, .8)+i*i+T*.5+.1*uv.x);
    vec2 p = uv;
    float d = length(p);
    vec3 c1 = mix(navy, purple, sin(i*.7+T*.3)*.5+.5);
    vec3 c2 = mix(teal, coral,  sin(i*.4+T*.2)*.5+.5);
    vec3 brandCol = mix(c1, c2, sin(i*.3)*.5+.5);
    col += .00125/d*(brandCol+.3);
    float b = noise(i+p+bg*1.731);
    col += .002*b/length(max(p, vec2(b*p.x*.02, p.y)));
    col  = mix(col, navy*bg*.6 + purple*bg*.15, d*.6);
  }
  O = vec4(col, 1);
}`;

const useShaderBackground = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl2');
    if (!gl) return;

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };

    const vs = compile(gl.VERTEX_SHADER,
      `#version 300 es\nprecision highp float;\nin vec4 position;\nvoid main(){ gl_Position=position; }`);
    const fs = compile(gl.FRAGMENT_SHADER, defaultShaderSource);

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,1,-1,-1,1,1,1,-1]), gl.STATIC_DRAW);
    const pos = gl.getAttribLocation(prog, 'position');
    gl.enableVertexAttribArray(pos);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 0, 0);

    const uRes  = gl.getUniformLocation(prog, 'resolution');
    const uTime = gl.getUniformLocation(prog, 'time');
    const uMove = gl.getUniformLocation(prog, 'move');
    const uTouch= gl.getUniformLocation(prog, 'touch');
    const uPC   = gl.getUniformLocation(prog, 'pointerCount');
    const uPts  = gl.getUniformLocation(prog, 'pointers');

    const resize = () => {
      const dpr = Math.max(1, 0.5 * window.devicePixelRatio);
      canvas.width  = window.innerWidth  * dpr;
      canvas.height = window.innerHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    const loop = (now) => {
      gl.clearColor(0,0,0,1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, now * 1e-3);
      gl.uniform2f(uMove, 0, 0);
      gl.uniform2f(uTouch, 0, 0);
      gl.uniform1i(uPC, 0);
      gl.uniform2fv(uPts, [0,0]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafRef.current);
      gl.deleteProgram(prog);
      gl.deleteBuffer(buf);
    };
  }, []);

  return canvasRef;
};

// ─── 404 Page ────────────────────────────────────────────────────────────────
const NotFound = () => {
  const navigate = useNavigate();
  const canvasRef = useShaderBackground();

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#0F1C2E] flex flex-col items-center justify-center">
      {/* Shader background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full touch-none"
        style={{ background: '#0F1C2E' }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0F1C2E]/50 pointer-events-none" />

      {/* Content */}
      <div
        className="relative z-10 flex flex-col items-center text-center px-6"
        style={{ animation: 'nf404FadeIn 0.9s ease-out both' }}
      >
        {/* Logo */}
        <img
          src="/logo-dark-coral.svg"
          alt="Mikel Media"
          className="h-14 w-auto mb-14"
        />

        {/* 404 giant number */}
        <div
          className="font-poppins font-black select-none leading-none mb-4"
          style={{
            fontSize: 'clamp(7rem, 22vw, 18rem)',
            background: 'linear-gradient(135deg, #7D468C 0%, #4EBFBF 50%, #F2785C 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 0 60px rgba(78,191,191,0.25))',
          }}
        >
          404
        </div>

        {/* Message */}
        <p
          className="font-poppins font-semibold text-xl sm:text-2xl text-white/90 mb-3"
          style={{ animation: 'nf404FadeIn 0.9s 0.15s ease-out both', opacity: 0 }}
        >
          Page not found
        </p>
        <p
          className="text-white/50 text-base sm:text-lg max-w-md mb-12 font-source-sans"
          style={{ animation: 'nf404FadeIn 0.9s 0.3s ease-out both', opacity: 0 }}
        >
          Looks like this page drifted off. Let's get you back on track.
        </p>

        {/* Liquid glass CTA */}
        <button
          onClick={() => navigate('/')}
          style={{
            animation: 'nf404FadeIn 0.9s 0.45s ease-out both',
            opacity: 0,
            /* liquid glass base */
            background: 'linear-gradient(135deg, rgba(125,70,140,0.35) 0%, rgba(78,191,191,0.25) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(78,191,191,0.35)',
            boxShadow: [
              '0 8px 32px rgba(78,191,191,0.15)',
              '0 2px 8px rgba(125,70,140,0.2)',
              'inset 0 1px 0 rgba(255,255,255,0.15)',
              'inset 0 -1px 0 rgba(0,0,0,0.1)',
            ].join(', '),
          }}
          className="group relative overflow-hidden px-10 py-4 rounded-full font-poppins font-semibold text-base tracking-wide text-white transition-[transform,box-shadow,background-color,border-color,color,opacity,gap,backdrop-filter] duration-300 hover:scale-105 hover:shadow-[0_12px_40px_rgba(78,191,191,0.3)]"
          onMouseEnter={e => {
            e.currentTarget.style.background =
              'linear-gradient(135deg, rgba(125,70,140,0.5) 0%, rgba(78,191,191,0.4) 100%)';
            e.currentTarget.style.borderColor = 'rgba(78,191,191,0.6)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background =
              'linear-gradient(135deg, rgba(125,70,140,0.35) 0%, rgba(78,191,191,0.25) 100%)';
            e.currentTarget.style.borderColor = 'rgba(78,191,191,0.35)';
          }}
        >
          {/* Gloss highlight */}
          <span
            className="absolute inset-x-0 top-0 h-1/2 rounded-t-full pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)',
            }}
          />
          <span className="relative flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="transition-transform group-hover:-translate-x-1">
              <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Home
          </span>
        </button>
      </div>

      <style>{`
        @keyframes nf404FadeIn {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NotFound;
